import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { setPrevState } from "../../../../../../../../util/common";
import { regexDelimiters, regexFileName } from "../../../../../../../../util/regularExpress";
import SmallToolbar from "../../../../../../../Common/SmallToolbar";

const MAX_FILE_SIZE = 5120;
const MIN_FILE_SIZE = 0;

const BrowserAsTarget = ({ exchangeJobState, setExchangeJobState, previousState }) => {
  const { compressionFormats, delimiters, salesforceDelimiters, fileTypes, enclosedByOptions } = useSelector(
    (state) => state.config
  );

  const {
    sourceType,
    hasHeaderRow,
    fileType,
    delimiter,
    filename,
    customDelimeter,
    loadingDropBoxValues,
    compression,
    singleFile,
    maxFileSize,
    fieldEnclosedBy,
  } = exchangeJobState;

  const compressionOptions = useMemo(
    () => (fileType === "csv" ? compressionFormats : compressionFormats.filter(({ value }) => value !== "gzip")),
    [compressionFormats, fileType]
  );

  const showCompression = useMemo(
    () => sourceType?.toLowerCase() === "snowflake" && fileType !== "parquet",
    [fileType, sourceType]
  );

  const showTextQualifier = useMemo(
    () => ["snowflake", "mysql"].includes(sourceType?.toLowerCase()) && fileType !== "parquet",
    [fileType, sourceType]
  );

  const showHasHeaderRow = useMemo(
    () => ["snowflake", "mysql"].includes(sourceType?.toLowerCase()) && fileType === "csv",
    [fileType, sourceType]
  );

  const $delimiters = useMemo(
    () => (sourceType.toLowerCase() === "salesforce" ? salesforceDelimiters : delimiters),
    [delimiters, salesforceDelimiters, sourceType]
  );

  return (
    <>
      <SmallToolbar item xs={12} sx={{ color: "#ed6c02", paddingBottom: "10px" }}>
        Export as
      </SmallToolbar>
      <Grid size={12}>
        <Grid container spacing={2} sx={{ paddingBottom: "10px" }}>
          <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
            <TextField
              value={filename}
              inputProps={{ maxLength: 200 }}
              required
              fullWidth
              label="File name"
              onChange={(e) => {
                if (regexFileName(e.target.value)) setPrevState(setExchangeJobState, { filename: e.target.value });
                if (e.target.value === "") setPrevState(setExchangeJobState, { filename: "" });
              }}
            />
          </Grid>
          {showCompression && (
            <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Compression</InputLabel>
                <Select
                  value={compression}
                  label="Compression"
                  onChange={(e) => setPrevState(setExchangeJobState, { compression: e.target.value })}
                >
                  {compressionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {showTextQualifier && (
            <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Text Qualifier</InputLabel>
                <Select
                  label="Text Qualifier"
                  value={fieldEnclosedBy}
                  onChange={(e) =>
                    setPrevState(setExchangeJobState, {
                      fieldEnclosedBy: e.target.value,
                      isFieldsConfigured:
                        e.target.value !== previousState.fieldEnclosedBy ? false : exchangeJobState.isFieldsConfigured,
                    })
                  }
                >
                  {enclosedByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2} sx={{ paddingBottom: "10px" }}>
          <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
            <TextField
              value={
                ["salesforce", "veevavault"].includes(sourceType.toLowerCase()) && fileType === "parquet"
                  ? "csv"
                  : fileType
              }
              required
              fullWidth
              label="Type"
              onChange={(e) => {
                setPrevState(setExchangeJobState, {
                  fileType: e.target.value,
                  ...(e.target.value === "parquet" && compression === "gzip"
                    ? { compression: compressionOptions[0].value }
                    : {}),
                });
              }}
              select
              disabled={loadingDropBoxValues}
            >
              {fileTypes.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    ["salesforce", "veevavault", "mysql"].includes(sourceType.toLowerCase()) &&
                    option.value === "parquet"
                  }
                >
                  {option.text}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {fileType === "csv" && (
            <>
              <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
                <TextField
                  value={delimiter}
                  required
                  fullWidth
                  label="Delimiter"
                  onChange={(e) => {
                    if (e.target.value !== "custom") setPrevState(setExchangeJobState, { customDelimeter: "" });
                    setPrevState(setExchangeJobState, { delimiter: e.target.value });
                  }}
                  select
                  disabled={loadingDropBoxValues}
                >
                  {$delimiters.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {
                /* Added input for custom delimiter on "Custom" option selection from "Format Delimiter" for txt file support */
                delimiter === "custom" && (
                  <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
                    <TextField
                      required
                      label="Custom value"
                      value={customDelimeter}
                      onChange={(e) => {
                        setPrevState(setExchangeJobState, {
                          customDelimeter: regexDelimiters(e.target.value)
                            ? e.target.value
                            : exchangeJobState.customDelimeter,
                        });
                      }}
                      inputProps={{ maxLength: 2 }}
                      fullWidth
                    />
                  </Grid>
                )
              }
            </>
          )}
          {showHasHeaderRow && (
            <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
              <FormGroup
                sx={{
                  paddingTop: "10px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasHeaderRow}
                      onChange={(e) =>
                        setPrevState(setExchangeJobState, {
                          hasHeaderRow: !hasHeaderRow,
                          isFieldsConfigured:
                            e.target.value !== previousState.hasHeaderRow ? false : exchangeJobState.isFieldsConfigured,
                        })
                      }
                    />
                  }
                  label="Header row"
                />
              </FormGroup>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} sx={{ paddingBottom: "10px" }}>
          {sourceType?.toLowerCase() === "snowflake" && (
            <>
              <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
                <TextField
                  type="number"
                  inputProps={{ min: MIN_FILE_SIZE, max: MAX_FILE_SIZE }}
                  value={maxFileSize}
                  required
                  fullWidth
                  label="Max file size (In MB)"
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    if (inputValue !== "")
                      inputValue =
                        Number(inputValue) > MAX_FILE_SIZE
                          ? String(MAX_FILE_SIZE)
                          : Number(inputValue) < MIN_FILE_SIZE
                          ? String(MIN_FILE_SIZE)
                          : e.target.value;

                    setPrevState(setExchangeJobState, { maxFileSize: inputValue });
                  }}
                />
              </Grid>
              <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
                <FormGroup
                  sx={{
                    paddingTop: "10px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={singleFile}
                        onChange={(e) =>
                          setPrevState(setExchangeJobState, {
                            singleFile: !singleFile,
                          })
                        }
                      />
                    }
                    label="Single File"
                  />
                </FormGroup>
              </Grid>
            </>
          )}
        </Grid>

        {sourceType?.toLowerCase() === "snowflake" && (
          <Grid
            justifyContent="flex-start"
            display="flex"
            sx={{ paddingTop: "0px !important" }}
            size={12}>
            <Alert
              variant="outlined"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ mt: 1.5, pt: 0, pb: 0, borderRadius: 20 }}
            >
              When a single file is selected, it will not be exported if it is larger than maximum file size.
            </Alert>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default BrowserAsTarget;
